import axios from 'axios';

import { useConfigTransformer } from '../config';
import { useRequest } from '../providers';
import type { REQUEST, RequestDefinition, RequestExtend } from '../types';
import { deepmergeByReference } from '../utils/deep-merge';

export function useRequestDefinition<C, O, E = undefined>(definition: RequestDefinition<C, O, E>) {
  return (config?: RequestExtend<O, E>) => {
    const axiosInstance = axios.create();

    const _def = definition instanceof Function ? definition(axiosInstance) : definition;
    const _conf = config instanceof Function ? config(axiosInstance) : config;

    return () => {
      const plugin = useRequest();

      const definitionConfig = (plugin.config[_def.id] || {}) as E;

      if (!definitionConfig) {
        throw new Error(`Missing configuration for ${_def.id} client`);
      }

      const mergeConfig = deepmergeByReference(plugin.config.globalConfig, definitionConfig);
      const transformer = useConfigTransformer<E>(_def.id as REQUEST);

      const entry = transformer ? transformer(mergeConfig) : (mergeConfig as E);

      const defOptions = (_def.options instanceof Function ? _def.options(entry) : _def.options) ?? Object.create(null);
      const extOptions =
        (_conf?.options instanceof Function ? _conf.options(entry) : _conf?.options) ?? Object.create(null);

      const _opt = deepmergeByReference(defOptions, extOptions);

      return new _def.client(axiosInstance, _opt);
    };
  };
}
